/**
 * PageRail — the page's actions as a vertical navy rail on the right edge.
 *
 * Ported from the QMS ticket action rail (qms: apps/TicketApp/components/TicketActionRail.jsx):
 * same 38px tabs, same navy, hairline separators, left tooltips, the active tab blue, a
 * collapse chevron as the last tab. Generic here: the page passes its own items.
 *
 * Only page-level actions belong on it (refresh, export, print, full screen, audit trail,
 * history, settings …). Submit / Sign / Approve / Save and row actions stay where they are.
 *
 * items: [{
 *   key,               // required, unique
 *   icon,              // a lucide component (Download) or an element (<Download size={18} />)
 *   label,             // tooltip + aria-label
 *   onClick,           // the same handler the old button had
 *   active,            // pressed/selected state → blue tab, aria-pressed
 *   disabled, loading, // loading spins the icon and disables the tab
 *   badge,             // number/string → count badge; true → plain dot
 *   tone,              // badge colour: 'block' (red, default) | 'warn' | 'plain'
 *   dot,               // a CSS colour → renders a status dot instead of an icon (not clickable)
 *   menu,              // antd Dropdown menu items → the tab opens a menu (e.g. "More")
 *   render,            // escape hatch: (className) => node, for a tab that owns its own control
 *   testId,
 * } | null | false]
 *
 * collapsible (default true): a last ‹/› tab folds the rail to that one tab; remembered per viewer.
 */
import React, { useEffect, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePageChrome } from './PageChromeContext';
import './PageRail.css';

const COLLAPSE_KEY = 'page-rail.collapsed';
const readCollapsed = () => {
  try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
};
const writeCollapsed = (v) => {
  try { localStorage.setItem(COLLAPSE_KEY, v ? '1' : '0'); } catch { /* private window */ }
};

function renderIcon(icon, spinning) {
  if (!icon) return null;
  const cls = spinning ? 'page-rail-spin' : undefined;
  if (React.isValidElement(icon)) {
    return spinning ? <span className={cls} style={{ display: 'flex' }}>{icon}</span> : icon;
  }
  const Icon = icon;
  return <Icon size={18} strokeWidth={1.9} className={cls} />;
}

export default function PageRail({ items = [], collapsible = true, ariaLabel = 'Page actions' }) {
  const { registerRail } = usePageChrome();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const shown = items.filter(Boolean);

  // Tell the layout a rail is up, so the content keeps a gutter clear for it — only while it
  // actually shows tabs; an empty rail renders nothing and must not reserve the strip.
  const hasItems = shown.length > 0;
  useEffect(() => (hasItems ? registerRail() : undefined), [hasItems, registerRail]);

  if (!shown.length) return null;

  const toggle = () => setCollapsed((v) => { writeCollapsed(!v); return !v; });

  const tab = (item) => {
    if (item.render) return <React.Fragment key={item.key}>{item.render('page-rail-btn')}</React.Fragment>;

    if (item.dot) {
      return (
        <Tooltip key={item.key} title={item.label} placement="left">
          <div className="page-rail-btn static" role="img" aria-label={item.label} data-testid={item.testId}>
            <span className="page-rail-dot" style={{ background: item.dot }} />
          </div>
        </Tooltip>
      );
    }

    const badge = item.badge === true
      ? <span className={`page-rail-badge empty ${item.tone || ''}`} />
      : item.badge || item.badge === 0
        ? (item.badge === 0 ? null : <span className={`page-rail-badge ${item.tone || ''}`}>{item.badge}</span>)
        : null;

    const isDisabled = Boolean(item.disabled || item.loading);
    const rawButton = (
      <button
        type="button"
        aria-label={item.label}
        aria-pressed={item.active === undefined ? undefined : Boolean(item.active)}
        className={`page-rail-btn ${item.active ? 'on' : ''}`}
        onClick={item.menu ? undefined : item.onClick}
        disabled={isDisabled}
        data-testid={item.testId}
      >
        {renderIcon(item.icon, item.loading)}
        {badge}
      </button>
    );
    // A disabled <button> fires no mouse events, so its tooltip would never show; a span
    // wrapper takes the hover instead (the antd-recommended pattern).
    const button = isDisabled
      ? <span className="page-rail-btnwrap" style={{ display: 'block', cursor: 'not-allowed' }}>{React.cloneElement(rawButton, { style: { pointerEvents: 'none' } })}</span>
      : rawButton;

    if (item.menu) {
      return (
        <Dropdown key={item.key} menu={item.menu} trigger={['click']} placement="bottomRight" disabled={item.disabled}>
          <Tooltip title={item.label} placement="left">{button}</Tooltip>
        </Dropdown>
      );
    }
    return <Tooltip key={item.key} title={item.label} placement="left">{button}</Tooltip>;
  };

  return (
    <nav className="page-rail" aria-label={ariaLabel} data-testid="page-rail">
      {!collapsed && shown.map(tab)}
      {collapsible && (
        <Tooltip title={collapsed ? 'Show page actions' : 'Hide page actions'} placement="left">
          <button
            type="button"
            className="page-rail-btn"
            aria-label={collapsed ? 'Show page actions' : 'Hide page actions'}
            aria-expanded={!collapsed}
            onClick={toggle}
            data-testid="page-rail-collapse"
          >
            {collapsed ? <ChevronLeft size={19} /> : <ChevronRight size={19} />}
          </button>
        </Tooltip>
      )}
    </nav>
  );
}
